<script setup>
const config = useRuntimeConfig()
const { find } = useStrapi()
const user = useStrapiUser()

const {
  isSupported,
  isRegistered,
  getEpisodeStats,
  deleteEpisode,
  downloadEpisode,
  cancelDownload,
  downloadProgress,
  formatSize,
  clearCache,
  register,
  autoCache,
  setAutoCache
} = useHLSCache()

const episodes = ref({})
const totalSize = ref(0)
const totalSegments = ref(0)
const loading = ref(true)
const animeData = ref({})
const error = ref(null)

// Запит токена для приватних аніме
const requestVideoToken = async () => {
  if (!import.meta.client) return
  if (!user?.value?.privat) return

  try {
    // console.log('[Downloads] Requesting video token for private anime')
    const videoTokenData = await find('video-token')
    if (videoTokenData) {
      window.videoToken = videoTokenData?.data?.attributes?.token
      // console.log('[Downloads] Video token set:', window.videoToken ? 'YES' : 'NO')
    }
  } catch (er) {
    console.error('[Downloads] Error getting video token:', er)
  }
}

// Запитуємо токен при монтуванні якщо користувач має доступ
onMounted(async () => {
  if (user?.value?.privat) {
    await requestVideoToken()
  }
})

const loadStats = async () => {
  loading.value = true
  error.value = null
  try {
    if (isSupported.value && !isRegistered.value) {
      await register()
    }
    const stats = await getEpisodeStats()
    if (stats) {
      episodes.value = stats
      totalSize.value = Object.values(stats).reduce(
        (sum, ep) => sum + ep.totalSize,
        0
      )
      totalSegments.value = Object.values(stats).reduce(
        (sum, ep) => sum + ep.segments.length,
        0
      )

      const uniqueSlugs = [
        ...new Set(Object.values(stats).map((ep) => ep.animeSlug || ep.animeId))
      ]
      await Promise.all(
        uniqueSlugs
          .filter((s) => s && s !== 'unknown' && !animeData.value[s])
          .map(async (slug) => {
            try {
              const anime = await $fetch(
                `${config.public.apiBase}/api/anime/${slug}`
              )
              animeData.value = { ...animeData.value, [slug]: anime }
            } catch (e) {}
          })
      )
    } else {
      episodes.value = {}
    }
  } catch (err) {
    error.value = err.message
  } finally {
    loading.value = false
  }
}

// Реал-тайм: мержимо прогрес завантаження в episodes
// Коли завантаження завершується — перезавантажуємо статистику
watch(
  downloadProgress,
  async (val) => {
    for (const [key, prog] of Object.entries(val)) {
      const epKey = `${prog.animeId}|||${prog.episodeId}`

      if (prog.status === 'downloading' && episodes.value[epKey]) {
        // Оновлюємо кількість сегментів в реал-тайм
        episodes.value = {
          ...episodes.value,
          [epKey]: {
            ...episodes.value[epKey],
            segments: Array(prog.done).fill(''),
            totalSegments: prog.total || episodes.value[epKey].totalSegments
          }
        }
      }

      if (prog.status === 'done') {
        // Перезавантажуємо після завершення
        await loadStats()
      }
    }
  },
  { deep: true }
)

const handleDeleteEpisode = async (animeId, episodeId) => {
  if (!confirm('Видалити цей епізод з кешу?')) return
  try {
    await deleteEpisode(animeId, episodeId)
    await loadStats()
  } catch (err) {}
}

const handleClearAll = async () => {
  if (!confirm('Видалити всі завантажені епізоди?')) return
  try {
    await clearCache()
    await loadStats()
  } catch (err) {}
}

const handleRedownload = (episode) => {
  let hlsUrl = episode.hlsUrl
  if (!hlsUrl) return

  const anime = getAnimeInfo(episode)

  // Для приватних аніме додаємо токен
  if (anime?.privat && import.meta.client && window.videoToken) {
    const url = new URL(hlsUrl)
    if (!url.searchParams.has('t')) {
      url.searchParams.set('t', window.videoToken)
      hlsUrl = url.toString()
    }
  }

  const epIdx = parseInt(episode.episodeId) - 1
  downloadEpisode(
    episode.animeId,
    episode.episodeId,
    episode.animeSlug,
    hlsUrl,
    {
      animeTitle: anime?.title || episode.animeSlug,
      episodeTitle:
        anime?.episodes?.[epIdx]?.title || `Серія ${episode.episodeId}`,
      posterUrl: anime?.poster?.url
        ? `${config.public.apiBase}${anime.poster.url}?format=webp&width=80&quality=70`
        : null
    }
  )
}

const getAnimeInfo = (episode) => {
  const slug = episode.animeSlug || episode.animeId
  return animeData.value[slug] || null
}

const getAnimeTitle = (episode) => {
  const anime = getAnimeInfo(episode)
  return anime?.title || episode.animeSlug || episode.animeId
}

const getEpisodeTitle = (episode) => {
  const anime = getAnimeInfo(episode)
  if (!anime?.episodes) return `Серія ${episode.episodeId}`
  const idx = parseInt(episode.episodeId) - 1
  return anime.episodes[idx]?.title || `Серія ${episode.episodeId}`
}

const getAnimePoster = (episode) => {
  const anime = getAnimeInfo(episode)
  if (!anime?.poster?.url) return null
  return `${config.public.apiBase}${anime.poster.url}?format=webp&width=200&quality=80`
}

const getWatchLink = (episode) => {
  const slug = episode.animeSlug || episode.animeId
  const epIndex = parseInt(episode.episodeId) - 1
  return `/watch/${slug}?ep=${epIndex}`
}

const isKnownComplete = (episode) => {
  if (!episode.totalSegments) return false
  const percent = (episode.segments.length / episode.totalSegments) * 100
  return percent >= 95
}

const getDownloadPercent = (episode) => {
  if (!episode.totalSegments || episode.totalSegments === 0) return null
  return Math.round((episode.segments.length / episode.totalSegments) * 100)
}

// Прогрес активного завантаження для конкретного епізоду
const getActiveProgress = (episode) => {
  const key = `${episode.animeId}-${episode.episodeId}`
  return downloadProgress.value[key] || null
}

const isDownloadingEpisode = (episode) => {
  const p = getActiveProgress(episode)
  return p?.status === 'downloading' || p?.status === 'fetching_playlist'
}

// ETA для картки в списку
const getETA = (episode) => {
  const p = getActiveProgress(episode)
  if (
    !p ||
    !isDownloadingEpisode(episode) ||
    !p.startTime ||
    !p.done ||
    !p.total
  )
    return null
  const elapsed = (Date.now() - p.startTime) / 1000
  if (elapsed < 2) return null
  const rate = p.done / elapsed
  if (rate <= 0) return null
  const etaSec = Math.round((p.total - p.done) / rate)
  if (etaSec <= 0) return null
  if (etaSec < 60) return `~${etaSec}с`
  const m = Math.floor(etaSec / 60)
  const s = etaSec % 60
  return s > 0 ? `~${m}хв ${s}с` : `~${m}хв`
}

onMounted(async () => {
  await loadStats()
})
</script>

<template>
  <div class="mx-auto px-[10px] py-[20px] max-w-[1350px] min-h-screen">
    <Head>
      <Title>Завантажені епізоди - Олсіор дивиться Аніме</Title>
    </Head>

    <div
      class="flex lg:flex-row flex-col lg:justify-between gap-[20px] mb-[20px]"
    >
      <div>
        <h1 class="font-bold text-3xl">
          Завантажені епізоди
        </h1>
        <p class="mt-1 text-sm text-base-content/60">
          Серії збережені для офлайн перегляду
        </p>
        <!-- Автозбереження: переглянуте само лягає в кеш. Вимкнули — у кеш
             потрапляє тільки завантажене кнопкою «Завантажити офлайн», а вже
             збережене лишається й грає як раніше -->
        <label
          v-if="isSupported"
          class="flex items-center gap-3 mt-3 cursor-pointer"
        >
          <input
            type="checkbox"
            class="toggle toggle-primary toggle-sm"
            :checked="autoCache"
            @change="setAutoCache($event.target.checked)"
          />
          <span class="text-sm">
            Автоматично зберігати переглянуте
            <span class="block text-xs text-base-content/50">
              {{
                autoCache
                  ? 'Серії, які ви дивитесь, зберігаються для офлайн-перегляду самі'
                  : 'Зберігається лише те, що ви завантажили вручну'
              }}
            </span>
          </span>
        </label>
      </div>
      <div class="flex items-center gap-[10px]">
        <button
          v-if="Object.keys(episodes).length > 0"
          @click="handleClearAll"
          class="flex items-center gap-[8px] bg-error/20 hover:bg-error/30 px-[14px] py-[7px] border border-error/20 rounded-md font-medium text-error text-sm transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="15"
            height="15"
            viewBox="0 0 24 24"
          >
            <path
              fill="currentColor"
              d="M7 21q-.825 0-1.412-.587T5 19V6H4V4h5V3h6v1h5v2h-1v13q0 .825-.587 1.413T17 21zM17 6H7v13h10zM9 17h2V8H9zm4 0h2V8h-2zM7 6v13z"
            />
          </svg>
          Очистити кеш
        </button>
        <button
          @click="loadStats"
          :disabled="loading"
          class="flex items-center gap-[8px] bg-neutral/60 px-[14px] py-[7px] border border-neutral-content/10 hover:border-neutral-content/30 rounded-md font-medium text-neutral-content/70 hover:text-neutral-content text-sm transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            :class="{ 'animate-spin': loading }"
          >
            <path
              fill="currentColor"
              d="M17.65 6.35A7.958 7.958 0 0 0 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4z"
            />
          </svg>
          Оновити
        </button>
      </div>
    </div>

    <!-- Не підтримується -->
    <div
      v-if="!isSupported"
      class="flex items-center gap-3 bg-warning/20 px-4 py-3 border border-warning/20 rounded-md text-warning text-sm"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="w-5 h-5 shrink-0"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
      <span>Ваш браузер не підтримує Service Worker.</span>
    </div>

    <!-- Помилка -->
    <div
      v-else-if="error"
      class="flex items-center gap-3 bg-error/20 px-4 py-3 border border-error/20 rounded-md text-error text-sm"
    >
      <span>Помилка: {{ error }}</span>
      <button
        @click="loadStats"
        class="bg-error/20 hover:bg-error/30 ml-auto px-3 py-1 rounded text-xs transition-colors"
      >
        Повторити
      </button>
    </div>

    <!-- Завантаження -->
    <div
      v-else-if="loading"
      class="flex justify-center items-center py-20"
    >
      <span class="loading loading-spinner loading-lg"></span>
    </div>

    <template v-else>
      <!-- Статистика -->
      <div
        v-if="Object.keys(episodes).length > 0"
        class="gap-3 grid grid-cols-3 mb-[20px]"
      >
        <div
          class="flex flex-col justify-center items-center bg-neutral/60 p-4 border border-neutral-content/10 rounded-md text-center"
        >
          <div class="font-bold text-2xl">
            {{ Object.keys(episodes).length }}
          </div>
          <div class="mt-1 text-xs text-base-content/50">Серій</div>
        </div>
        <div
          class="flex flex-col justify-center items-center bg-neutral/60 p-4 border border-neutral-content/10 rounded-md text-center"
        >
          <div class="font-bold text-2xl">{{ totalSegments }}</div>
          <div class="mt-1 text-xs text-base-content/50">Сегментів</div>
        </div>
        <div
          class="flex flex-col justify-center items-center bg-neutral/60 p-4 border border-neutral-content/10 rounded-md text-center"
        >
          <div class="font-bold text-2xl">{{ formatSize(totalSize) }}</div>
          <div class="mt-1 text-xs text-base-content/50">Місця</div>
        </div>
      </div>

      <!-- Порожній стан -->
      <div
        v-if="Object.keys(episodes).length === 0"
        class="py-20 text-center"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="inline-block opacity-20 mb-4 w-20 h-20"
          viewBox="0 0 24 24"
        >
          <path
            fill="currentColor"
            d="M19 9h-4V3H9v6H5l7 7zm-8 2V5h2v6h1.17L12 13.17L9.83 11zm-6 8v-2h14v2z"
          />
        </svg>
        <h2 class="font-bold text-xl">Немає завантажених серій</h2>
        <p class="mt-2 text-sm text-base-content/60">
          Серії кешуються автоматично під час перегляду.<br />
          Або натисніть "Завантажити офлайн" в налаштуваннях плеєра.
        </p>
        <NuxtLink
          to="/"
          class="inline-flex items-center gap-2 bg-primary/80 hover:bg-primary mt-6 px-[16px] py-[8px] border border-primary/60 rounded-md font-medium text-primary-content text-sm transition-colors"
        >
          Переглянути аніме
        </NuxtLink>
      </div>

      <!-- Список серій -->
      <div
        v-else
        class="gap-[10px] grid grid-cols-1"
      >
        <div
          v-for="(episode, key) in episodes"
          :key="key"
          class="flex flex-row bg-neutral/60 shadow border border-neutral-content/10 rounded-lg lg:min-h-[140px] overflow-hidden"
        >
          <!-- Постер -->
          <div class="w-[100px] lg:w-[100px] h-full shrink-0">
            <NuxtImg
              v-if="getAnimePoster(episode)"
              :src="getAnimePoster(episode)"
              :alt="getAnimeTitle(episode)"
              class="bg-base-300 w-full h-full object-cover"
              width="300"
              height="300"
            />
            <div
              v-else
              class="flex justify-center items-center bg-neutral/40 w-full h-32"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="opacity-20 w-8 h-8"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2m0 16H5V5h14z"
                />
              </svg>
            </div>
          </div>

          <!-- Інформація -->
          <div class="flex flex-col flex-1 justify-between p-[10px]">
            <div
              class="flex lg:flex-row flex-col justify-between items-start gap-[10px]"
            >
              <div class="flex flex-col flex-1 justify-between min-w-0 h-full">
                <div class="">
                  <div class="font-bold truncate">
                    {{ getAnimeTitle(episode) }}
                  </div>
                  <div
                    class="text-sm text-base-content text-opacity-50 truncate"
                  >
                    {{ getEpisodeTitle(episode) }}
                  </div>
                  <!-- Метадані -->
                  <div
                    class="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1.5 text-xs text-base-content text-opacity-50"
                  >
                    <span class="tabular-nums">
                      {{
                        isDownloadingEpisode(episode)
                          ? (getActiveProgress(episode)?.done ??
                            episode.segments.length)
                          : episode.segments.length
                      }}
                      <template
                        v-if="
                          episode.totalSegments ||
                          getActiveProgress(episode)?.total
                        "
                      >
                        /
                        {{
                          episode.totalSegments ||
                          getActiveProgress(episode)?.total
                        }}
                      </template>
                      сегм.
                    </span>
                    <span>{{ formatSize(episode.totalSize) }}</span>
                    <!-- Статус -->
                    <template v-if="isDownloadingEpisode(episode)">
                      <span class="font-medium tabular-nums text-primary">
                        {{
                          getActiveProgress(episode)?.total
                            ? Math.round(
                                (getActiveProgress(episode).done /
                                  getActiveProgress(episode).total) *
                                  100
                              )
                            : 0
                        }}%
                      </span>
                      <span
                        v-if="getETA(episode)"
                        class="text-primary"
                        >{{ getETA(episode) }}</span
                      >
                    </template>
                    <template v-else>
                      <span
                        v-if="!episode.hasPlaylist"
                        class="font-medium text-error"
                        >✗ Немає плейлиста</span
                      >
                      <span
                        v-else-if="isKnownComplete(episode)"
                        class="font-medium text-success"
                        >✓ Повна</span
                      >
                      <span
                        v-else-if="episode.totalSegments"
                        class="font-medium text-warning"
                      >
                        ⚠ {{ getDownloadPercent(episode) }}%
                      </span>
                      <span
                        v-else
                        class="text-base-content text-opacity-40"
                        >~ Частково</span
                      >
                    </template>
                  </div>
                </div>

                <!-- Прогрес бар під час завантаження -->
                <div v-if="isDownloadingEpisode(episode)">
                  <progress
                    class="w-full h-1 progress progress-primary"
                    :value="getActiveProgress(episode)?.done || 0"
                    :max="getActiveProgress(episode)?.total || 1"
                  ></progress>
                </div>
              </div>

              <!-- Кнопки -->
              <div class="flex flex-col gap-[8px] shrink-0">
                <NuxtLink
                  :to="getWatchLink(episode)"
                  class="flex items-center gap-[6px] bg-primary/80 hover:bg-primary px-[12px] py-[6px] border border-primary/60 rounded-md font-medium text-primary-content text-sm transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill="currentColor"
                      d="M8 5v14l11-7z"
                    />
                  </svg>
                  Дивитись
                </NuxtLink>

                <button
                  v-if="
                    !isKnownComplete(episode) &&
                    episode.hlsUrl &&
                    !isDownloadingEpisode(episode)
                  "
                  @click="handleRedownload(episode)"
                  class="flex items-center gap-[6px] bg-warning/20 hover:bg-warning/30 px-[12px] py-[6px] border border-warning/20 rounded-md font-medium text-warning text-sm transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill="currentColor"
                      d="M19 9h-4V3H9v6H5l7 7zm-8 2V5h2v6h1.17L12 13.17L9.83 11zm-6 8v-2h14v2z"
                    />
                  </svg>
                  Докачати
                </button>

                <button
                  v-if="isDownloadingEpisode(episode)"
                  @click="cancelDownload(episode.animeId, episode.episodeId)"
                  class="flex items-center gap-[6px] bg-error/20 hover:bg-error/30 px-[12px] py-[6px] border border-error/20 rounded-md font-medium text-error text-sm transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill="currentColor"
                      d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                    />
                  </svg>
                  Стоп
                </button>

                <button
                  @click="
                    handleDeleteEpisode(episode.animeId, episode.episodeId)
                  "
                  class="flex items-center gap-[6px] bg-error/20 hover:bg-error/30 px-[12px] py-[6px] border border-error/20 rounded-md font-medium text-error text-sm transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill="currentColor"
                      d="M7 21q-.825 0-1.412-.587T5 19V6H4V4h5V3h6v1h5v2h-1v13q0 .825-.587 1.413T17 21zM17 6H7v13h10zM9 17h2V8H9zm4 0h2V8h-2zM7 6v13z"
                    />
                  </svg>
                  Видалити
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <MyFooter />
  </div>
</template>
